import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { searchQueryState } from "@/state/SearchQuery";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { createRef, ref } from "lit/directives/ref.js";
import { TriStateOptions } from "@/components/search/filter_element";
import { initTooltips } from "@/utilities";

/**
 * This element represents a tri-state switch for search filters
 *
 * @element d-tri-switch-filter
 *
 * @prop {string} param - the searchQuery param to be used for this filter
 * @prop {TriStateOptions} default - which state is enabled when the param is absent
 */
@customElement("d-tri-switch-filter")
export class TriSwitchFilter extends DodonaElement {
    static get options(): readonly TriStateOptions[] {
        return [TriStateOptions.All, TriStateOptions.True, TriStateOptions.False];
    }

    @property({ type: String })
    param: string;

    @property({ type: String })
    default: TriStateOptions = TriStateOptions.All;

    private inputRef = createRef<HTMLInputElement>();

    private get inputId(): string {
        const safeParam = (this.param || "tri").replace(/[^a-zA-Z0-9_-]/g, "_");
        return `${safeParam}-filter`;
    }

    private get stateId(): string {
        return `${this.inputId}-state`;
    }

    private get ariaCheckedValue(): "true" | "false" | "mixed" {
        const value = this.getValue();
        if (value === TriStateOptions.All) return "mixed";
        return value === TriStateOptions.True ? "true" : "false";
    }

    private get label(): string {
        return I18n.t(`js.search.filter.${this.param}`);
    }

    private get tooltipText(): string {
        // i18n-tasks-use t('js.search.tri_state_filter.tooltip.all') t('js.search.tri_state_filter.tooltip.true') t('js.search.tri_state_filter.tooltip.false')
        return I18n.t(`js.search.tri_state_filter.tooltip.${this.getValue()}`, { label: this.label });
    }

    render(): TemplateResult {
        return html`
            <div class="switch-filter">
                <div
                    class="token token-bordered"
                    title="${this.tooltipText}"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    data-bs-trigger="hover"
                >
                    <input
                        type="checkbox"
                        id="${this.inputId}"
                        aria-checked="${this.ariaCheckedValue}"
                        aria-describedby="${this.stateId}"
                        ${ref(this.inputRef)}
                        @click=${this.handleCheckboxClick}
                    />
                    <label for="${this.inputId}">${this.label}</label>
                    <span id="${this.stateId}" class="visually-hidden">${this.tooltipText}</span>
                </div>
            </div>
        `;
    }

    protected updated(): void {
        const input = this.inputRef.value;
        if (!input) return;
        const value = this.getValue();
        input.checked = value === TriStateOptions.True;
        input.indeterminate = value === TriStateOptions.All;

        // Keep ARIA in sync even if something changes outside render()
        input.setAttribute("aria-checked", this.ariaCheckedValue);

        initTooltips(this);
    }

    private getValue(): TriStateOptions {
        if (!searchQueryState.queryParams.has(this.param)) {
            return this.default;
        }
        const val = searchQueryState.queryParams.get(this.param);
        if (TriSwitchFilter.options.includes(val as TriStateOptions)) {
            return val as TriStateOptions;
        }

        return this.default;
    }

    private setValue(val: TriStateOptions): void {
        searchQueryState.queryParams.set(this.param, val);
        this.requestUpdate();
    }

    private handleCheckboxClick = (): void => {
        const value = this.getValue();

        const index = TriSwitchFilter.options.indexOf(value);
        this.setValue(TriSwitchFilter.options[(index + 1) % TriSwitchFilter.options.length]);
    };
}
